// *********************************************************************************************
// * Assessment

import { ResidentialStatusLabel } from '../../../shared/models/enums/residential-status.enum';

export enum AssessmentStatus {
  InProgress = 'En cours de rédaction',
  ValidationCLI = 'Signé par le participant',
  Signed = 'En cours de validation',
  Validated = 'Validé',
}

export const assessmentStatusLabels: Map<AssessmentStatus, string> = new Map<AssessmentStatus, string>(
  Object.keys(AssessmentStatus).map((x: string) => [x, AssessmentStatus[x]] as [AssessmentStatus, string]),
);

// *********************************************************************************************
// * Context

export enum AssessmentContext {
  RioFaceToFace = 'RIO (présentiel)',
  RioPhone = 'RIO (téléphonique)',
  PhoneSecond = 'Entretien téléphonique (2ème convocation)',
  PhoneRegul = 'Entretien téléphonique de régularisation',
  BeginningAssessment = 'Premier diagnostic en début de parcours',
  AmendAssessment = 'Mise à jour du diagnostic',
}

export const assessmentContextLabels: Map<AssessmentContext, string> = new Map<AssessmentContext, string>(
  Object.keys(AssessmentContext).map((x: string) => [x, AssessmentContext[x]] as [AssessmentContext, string]),
);

export const assessmentContextSortOrders: Map<AssessmentContext, number> = new Map<AssessmentContext, number>([
  [AssessmentContext.RioFaceToFace, 0],
  [AssessmentContext.RioPhone, 1],
  [AssessmentContext.PhoneSecond, 2],
  [AssessmentContext.PhoneRegul, 3],
  [AssessmentContext.BeginningAssessment, 4],
  [AssessmentContext.AmendAssessment, 5],
]);

export function assessmentContextSort(a1: AssessmentContext, a2: AssessmentContext): number {
  return assessmentContextSortOrders[a1] - assessmentContextSortOrders[a2];
}

// *********************************************************************************************
// * In charge person - Category

export enum AssessmentInChargePersonCategory {
  Partner = 'Conjoint(e)',
  Child = 'Enfant à charge',
  Other = 'Autre',
}

export const assessmentInChargePersonCategoryLabels: Map<AssessmentInChargePersonCategory, string> = new Map<
  AssessmentInChargePersonCategory,
  string
>(
  Object.keys(AssessmentInChargePersonCategory).map(
    (x: string) => [x, AssessmentInChargePersonCategory[x]] as [AssessmentInChargePersonCategory, string],
  ),
);

export const assessmentInChargePersonCategorySortOrders: Map<AssessmentInChargePersonCategory, number> = new Map<
  AssessmentInChargePersonCategory,
  number
>([
  [AssessmentInChargePersonCategory.Partner, 0],
  [AssessmentInChargePersonCategory.Child, 1],
  [AssessmentInChargePersonCategory.Other, 2],
]);

export function AssessmentInChargePersonCategorySort(a1: AssessmentInChargePersonCategory, a2: AssessmentInChargePersonCategory): number {
  return assessmentInChargePersonCategorySortOrders[a1] - assessmentInChargePersonCategorySortOrders[a2];
}
// *********************************************************************************************
// * In charge person - Age period

export enum AssessmentInChargePersonAge {
  Period_0_2 = '0 - 2 ans',
  Period_3_5 = '3 - 5 ans',
  Period_6_11 = '6 - 11 ans',
  Period_12_18 = '12 - 18 ans',
  Period_19_Infinity = '> 18 ans',
}

export const assessmentInChargePersonAgeLabels: Map<AssessmentInChargePersonAge, string> = new Map<AssessmentInChargePersonAge, string>(
  Object.keys(AssessmentInChargePersonAge).map((x: string) => [x, AssessmentInChargePersonAge[x]] as [AssessmentInChargePersonAge, string]),
);

export const assessmentInChargePersonAgeSortOrders: Map<AssessmentInChargePersonAge, number> = new Map<AssessmentInChargePersonAge, number>([
  [AssessmentInChargePersonAge.Period_0_2, 0],
  [AssessmentInChargePersonAge.Period_3_5, 1],
  [AssessmentInChargePersonAge.Period_6_11, 2],
  [AssessmentInChargePersonAge.Period_12_18, 3],
  [AssessmentInChargePersonAge.Period_19_Infinity, 4],
]);

export function assessmentInChargePersonAgeSort(a1: AssessmentInChargePersonAge, a2: AssessmentInChargePersonAge): number {
  return assessmentInChargePersonAgeSortOrders[a1] - assessmentInChargePersonAgeSortOrders[a2];
}

// *********************************************************************************************
// * In charge person - Situation

export enum AssessmentInChargePersonSituation {
  Young = 'En bas-âge',
  InSchool = 'Scolarisé',
  Student = 'Etudiant / en formation',
  Employee = 'En emploi',
  Other = 'Autre',
}

export const assessmentInChargePersonSituationLabels: Map<AssessmentInChargePersonSituation, string> = new Map<
  AssessmentInChargePersonSituation,
  string
>(
  Object.keys(AssessmentInChargePersonSituation).map(
    (x: string) => [x, AssessmentInChargePersonSituation[x]] as [AssessmentInChargePersonSituation, string],
  ),
);

export const assessmentInChargePersonSituationSortOrders: Map<AssessmentInChargePersonSituation, number> = new Map<
  AssessmentInChargePersonSituation,
  number
>([
  [AssessmentInChargePersonSituation.Young, 0],
  [AssessmentInChargePersonSituation.InSchool, 1],
  [AssessmentInChargePersonSituation.Student, 2],
  [AssessmentInChargePersonSituation.Employee, 3],
  [AssessmentInChargePersonSituation.Other, 4],
]);

export function assessmentInChargePersonSituationSort(a1: AssessmentInChargePersonSituation, a2: AssessmentInChargePersonSituation): number {
  return assessmentInChargePersonSituationSortOrders[a1] - assessmentInChargePersonSituationSortOrders[a2];
}

// *********************************************************************************************
// * Activity

export enum AssessmentActivity {
  Employee = 'Salarié(e)',
  Farmer = 'Exploitant(e) agricole',
  SelfEmployed = 'Travailleur(se) indépendant(e)',
  CasualEmployment = 'En emploi occasionnel (ex Interim)',
}

export const assessmentActivityLabels: Map<AssessmentActivity, string> = new Map<AssessmentActivity, string>(
  Object.keys(AssessmentActivity).map((x: string) => [x, AssessmentActivity[x]] as [AssessmentActivity, string]),
);

export const assessmentActivityKeyFromLabel = (key: string): string => {
  return Object.entries(AssessmentActivity).find((entry) => entry[0] === key)?.[1];
};

export const assessmentActivitySortOrders: Map<AssessmentActivity, number> = new Map<AssessmentActivity, number>([
  [AssessmentActivity.Employee, 0],
  [AssessmentActivity.Farmer, 1],
  [AssessmentActivity.SelfEmployed, 2],
  [AssessmentActivity.CasualEmployment, 3],
]);

export function assessmentActivitySort(a1: AssessmentActivity, a2: AssessmentActivity): number {
  return assessmentActivitySortOrders[a1] - assessmentActivitySortOrders[a2];
}

// *********************************************************************************************
// * Unactivity

export enum AssessmentUnactivity {
  Formation = 'En formation',
  Other = 'Autre',
}

export const assessmentUnactivityLabels: Map<AssessmentUnactivity, string> = new Map<AssessmentUnactivity, string>(
  Object.keys(AssessmentUnactivity).map((x: string) => [x, AssessmentUnactivity[x]] as [AssessmentUnactivity, string]),
);

export const assessmentUnactivityKeyFromLabel = (key: string): string => {
  return Object.entries(AssessmentUnactivity).find((entry) => entry[0] === key)?.[1];
};

export const assessmentUnactivitySortOrders: Map<AssessmentUnactivity, number> = new Map<AssessmentUnactivity, number>([
  [AssessmentUnactivity.Formation, 0],
  [AssessmentUnactivity.Other, 1],
]);

export function assessmentUnactivitySort(a1: AssessmentUnactivity, a2: AssessmentUnactivity): number {
  return assessmentUnactivitySortOrders[a1] - assessmentUnactivitySortOrders[a2];
}

// *********************************************************************************************
// * Has worked

export enum AssessmentHowLongHasWorked {
  LessThanOneYear = "Il y a moins d'un an",
  BetweenOneAndTwoYears = 'Il y a 1 à 2 ans',
  BetweenTwoAndFiveYears = 'Il y a 2 à 5 ans',
  MoreThanFiveYears = 'Il y a + 5 ans',
}

export const assessmentHowLongHasWorkedLabels: Map<AssessmentHowLongHasWorked, string> = new Map<AssessmentHowLongHasWorked, string>(
  Object.keys(AssessmentHowLongHasWorked).map((x: string) => [x, AssessmentHowLongHasWorked[x]] as [AssessmentHowLongHasWorked, string]),
);

export const assessmentHowLongHasWorkedSortOrders: Map<AssessmentHowLongHasWorked, number> = new Map<AssessmentHowLongHasWorked, number>([
  [AssessmentHowLongHasWorked.LessThanOneYear, 0],
  [AssessmentHowLongHasWorked.BetweenOneAndTwoYears, 1],
  [AssessmentHowLongHasWorked.BetweenTwoAndFiveYears, 2],
  [AssessmentHowLongHasWorked.MoreThanFiveYears, 3],
]);

export function assessmentHowLongHasWorkedSort(a1: AssessmentHowLongHasWorked, a2: AssessmentHowLongHasWorked): number {
  return assessmentHowLongHasWorkedSortOrders[a1] - assessmentHowLongHasWorkedSortOrders[a2];
}

// *********************************************************************************************
// * Pro exp - Duration

export enum AssessmentProExpDuration {
  LessThanOneYear = '- de 1 an',
  BetweenOneAndTwoYears = 'de 1 à 2 ans',
  BetweenTwoAndFiveYears = 'de 3 à 5 ans',
  MoreThanFiveYears = '+ de 5 ans',
}

export const assessmentProExpDurationLabels: Map<AssessmentProExpDuration, string> = new Map<AssessmentProExpDuration, string>(
  Object.keys(AssessmentProExpDuration).map((x: string) => [x, AssessmentProExpDuration[x]] as [AssessmentProExpDuration, string]),
);

export const assessmentProExpDurationSortOrders: Map<AssessmentProExpDuration, number> = new Map<AssessmentProExpDuration, number>([
  [AssessmentProExpDuration.LessThanOneYear, 0],
  [AssessmentProExpDuration.BetweenOneAndTwoYears, 1],
  [AssessmentProExpDuration.BetweenTwoAndFiveYears, 2],
  [AssessmentProExpDuration.MoreThanFiveYears, 3],
]);

export function assessmentProExpDurationSort(a1: AssessmentProExpDuration, a2: AssessmentProExpDuration): number {
  return assessmentProExpDurationSortOrders[a1] - assessmentProExpDurationSortOrders[a2];
}

// *********************************************************************************************
// * Job enter registration

export enum AssessmentJobCenterRegistration {
  Yes = 'Oui',
  No = 'Non',
  InProgress = 'En cours',
}

export const assessmentJobCenterRegistrationLabels: Map<AssessmentJobCenterRegistration, string> = new Map([
  [AssessmentJobCenterRegistration.Yes, 'Oui'],
  [AssessmentJobCenterRegistration.No, 'Non'],
  [AssessmentJobCenterRegistration.InProgress, 'En cours'],
]);

export const assessmentJobCenterRegistrationKeyFromLabel = (key: string): string => {
  return Object.entries(AssessmentJobCenterRegistration).find((entry) => entry[0] === key)?.[1];
};

export const assessmentJobCenterRegistrationSortOrders: Map<AssessmentJobCenterRegistration, number> = new Map<
  AssessmentJobCenterRegistration,
  number
>([
  [AssessmentJobCenterRegistration.Yes, 0],
  [AssessmentJobCenterRegistration.No, 1],
  [AssessmentJobCenterRegistration.InProgress, 2],
]);

export function assessmentJobCenterRegistrationSort(a1: AssessmentJobCenterRegistration, a2: AssessmentJobCenterRegistration): number {
  return assessmentJobCenterRegistrationSortOrders[a1] - assessmentJobCenterRegistrationSortOrders[a2];
}

// *********************************************************************************************
// * Experience length

export enum AssessmentExperienceLength {
  NoExperience = `Pas d'expérience`,
  LessThanAYear = `- de 1 an`,
  Between1and3year = `de 1 à 3 ans`,
  MoreThanThreeYear = `+ de 3 ans`,
}

export const assessmentExperienceLengthLabels: Map<AssessmentExperienceLength, string> = new Map<AssessmentExperienceLength, string>(
  Object.keys(AssessmentExperienceLength).map((x: string) => [x, AssessmentExperienceLength[x]] as [AssessmentExperienceLength, string]),
);

export const assessmentExperienceLengthSortOrders: Map<AssessmentExperienceLength, number> = new Map<AssessmentExperienceLength, number>([
  [AssessmentExperienceLength.NoExperience, 0],
  [AssessmentExperienceLength.LessThanAYear, 1],
  [AssessmentExperienceLength.Between1and3year, 2],
  [AssessmentExperienceLength.MoreThanThreeYear, 3],
]);

export function assessmentExperienceLengthSort(a1: AssessmentExperienceLength, a2: AssessmentExperienceLength): number {
  return assessmentExperienceLengthSortOrders[a1] - assessmentExperienceLengthSortOrders[a2];
}

// *********************************************************************************************
// * Contract Types

export enum AssessmentContractTypes {
  CDD = 'CDD',
  CDI = 'CDI',
  Interim = 'Interim',
  HelpedContract = 'Contrat aidé',
  AlternatingContract = `Contrat d'alternance`,
  Internship = 'Stage',
  TemporaryWorkContract = `Contrat travail temporaire insertion`,
  Seasonnal = 'Saisonnier',
}

export const assessmentContractTypesLabels: Map<AssessmentContractTypes, string> = new Map<AssessmentContractTypes, string>(
  Object.keys(AssessmentContractTypes).map((x: string) => [x, AssessmentContractTypes[x]] as [AssessmentContractTypes, string]),
);

export const assessmentContractTypesSortOrders: Map<AssessmentContractTypes, number> = new Map<AssessmentContractTypes, number>([
  [AssessmentContractTypes.CDD, 0],
  [AssessmentContractTypes.CDI, 1],
  [AssessmentContractTypes.Interim, 2],
  [AssessmentContractTypes.HelpedContract, 3],
  [AssessmentContractTypes.AlternatingContract, 4],
  [AssessmentContractTypes.Internship, 5],
  [AssessmentContractTypes.TemporaryWorkContract, 6],
  [AssessmentContractTypes.Seasonnal, 7],
]);

export function assessmentContractTypesSort(a1: AssessmentContractTypes, a2: AssessmentContractTypes): number {
  return assessmentContractTypesSortOrders[a1] - assessmentContractTypesSortOrders[a2];
}

// *********************************************************************************************
// * Working Time

export enum AssessmentWorkingTime {
  FullTime = 'Temps plein',
  PartialTime = 'Temps partiel',
}

export const assessmentWorkingTimeLabels: Map<AssessmentWorkingTime, string> = new Map<AssessmentWorkingTime, string>(
  Object.keys(AssessmentWorkingTime).map((x: string) => [x, AssessmentWorkingTime[x]] as [AssessmentWorkingTime, string]),
);

export const assessmentWorkingTimeSortOrders: Map<AssessmentWorkingTime, number> = new Map<AssessmentWorkingTime, number>([
  [AssessmentWorkingTime.FullTime, 0],
  [AssessmentWorkingTime.PartialTime, 1],
]);

export function assessmentWorkingTimeSort(a1: AssessmentWorkingTime, a2: AssessmentWorkingTime): number {
  return assessmentWorkingTimeSortOrders[a1] - assessmentWorkingTimeSortOrders[a2];
}

// *********************************************************************************************
// * Residential status

export const residentialStatusLabels: Map<ResidentialStatusLabel, string> = new Map<ResidentialStatusLabel, string>(
  Object.keys(ResidentialStatusLabel).map((x: string) => [x, ResidentialStatusLabel[x]] as [ResidentialStatusLabel, string]),
);

// *********************************************************************************************
// * Support proposal

export enum AssessmentSupportProposal {
  ProSupport = 'Accompagnement professionnel (France Travail)',
  GlobalSupport = 'Accompagnement global (France travail et travailleur social)',
  SocioProSupport = 'Accompagnement socio-professionnel',
  SocialSupport = 'Accompagnement social',
}

export const assessmentSupportProposalLabels: Map<AssessmentSupportProposal, string> = new Map<AssessmentSupportProposal, string>(
  Object.keys(AssessmentSupportProposal).map((x: string) => [x, AssessmentSupportProposal[x]] as [AssessmentSupportProposal, string]),
);

export const assessmentSupportProposalSortOrders: Map<AssessmentSupportProposal, number> = new Map<AssessmentSupportProposal, number>([
  [AssessmentSupportProposal.ProSupport, 0],
  [AssessmentSupportProposal.GlobalSupport, 1],
  [AssessmentSupportProposal.SocioProSupport, 2],
  [AssessmentSupportProposal.SocialSupport, 3],
]);

export function assessmentSupportProposalSort(a1: AssessmentSupportProposal, a2: AssessmentSupportProposal): number {
  return assessmentSupportProposalSortOrders[a1] - assessmentSupportProposalSortOrders[a2];
}
